<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <h4 qid="rr-view-page-title" class="mb-3">
              {{ retentionRule.name }}
            </h4>
            <div class="mb-2">
              <span v-if="getGuide('threat')">
                <GuideButton :guide="getGuide('threat')"></GuideButton>
              </span>
            </div>
          </div>

          <div class="col-md-6 text-right" qid="view-rr-modified">
            <div class="mb-4">
              <span class="h6 ml-3">
                <span v-if="isGranted([permissions.RETENTION_RULE_EDIT, permissions.RETENTION_RULE_EDIT_OWN], retentionRule.created_by) && (!getClient.parent || (getClient.parent && retentionRule.partition))"
                      class="btn btn-edit"
                      qid="edit-rr-button"
                      @click="showEditRRModal(retentionRule)"
                >
                  <font-awesome-icon icon="pen"/>
                </span>
                <span v-if="isGranted([permissions.RETENTION_RULE_DELETE, permissions.RETENTION_RULE_DELETE_OWN], retentionRule.created_by) && (!getClient.parent || (getClient.parent && retentionRule.partition))"
                      class="btn btn-delete"
                      qid="delete-rr-button"
                      @click="showConfirmDeleteModal(retentionRule)"
                >
                  <font-awesome-icon icon="trash-alt"/>
                </span>
              </span>
            </div>
            <div>
              <span>{{ $t('table.last_updated') }}</span>
              <span class="ml-2 text-muted small" qid="view-rr-modified-date">
              {{ retentionRule.updated_at | formatDate }}
            </span>
              <Avatar v-if="retentionRule.updated_by" :object="retentionRule.updated_by" size="xs" class="ml-3 mr-2"></Avatar>
              <span v-if="retentionRule.updated_by" qid="view-rr-modified-by">
              {{ retentionRule.updated_by.name }}
            </span>
            </div>
          </div>

        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="content-view-body">
          <br>
          <div class="mt-3">
            <p class="text-dark">
              {{ $t('maintenance.threats.create.description_label') }}
            </p>
            <hr>
            <p qid="rr-description" class="text-formatted">
              {{ retentionRule.description }}
            </p>
            <br>
          </div>
          <div class="mt-3">
            <p class="text-dark">
              {{ $t('maintenance.threats.table.actors') }}
            </p>
            <hr>
            <p qid="rr-description">
              <span v-if="retentionRule.actors && retentionRule.actors.length" class="mr-2 ml-2">
                <span class="mr-1" v-for="actor in retentionRule.actors" qid="activity-tags-list">
                  <span class="badge badge-info mb-1">{{ actor.name }}</span>
                </span>
              </span>
            </p>
            <br>
          </div>
        </div>
      </div>
    </div>


    <!-- Edit retention rule modal template -->
    <b-modal ref="edit-retention-rule" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-t-title">
          {{ $t('maintenance.threats.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-t-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editRRFormSubmit" qid="edit-t-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-t-name-label">
              {{ $t('maintenance.threats.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-t-form-name"
              type="text"
              v-model="rrForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.threats.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-t-description-label">
              {{ $t('maintenance.threats.create.description_label') }}
            </label>
            <b-form-textarea
              qid="edit-t-form-description"
              v-model="rrForm.description"
              rows="6"
              max-rows="6"
              required
              :placeholder="$t('maintenance.threats.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <label qid="create-s-tags-label">
              {{ $t('maintenance.threats.create.actor_label') }}
            </label>
            <multiselect
                v-model="selectedActors"
                :options="actors"
                :multiple="true"
                :taggable="true"
                track-by="name"
                :tagPlaceholder="$t('maintenance.threats.create.actor_dropdown_label')"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('maintenance.threats.create.actor_placeholder')"
                label="name"
                qid="create-do-tags-select"
                @search-change="loadActors"
                @tag="addActor"
            ></multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditRRModal"
                    qid="edit-t-cancel-button"
          >
            {{ $t('maintenance.threats.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-t-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.threats.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- Delete retention rule confirm modal template -->
    <b-modal ref="delete-retention-rule" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-t-title">
          {{ $t('maintenance.threats.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.threats.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.threats.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-t-cancel-button"
        >
          {{ $t('maintenance.threats.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteRR(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-t-submit-button"
                  v-if="isGranted([permissions.RETENTION_RULE_DELETE, permissions.RETENTION_RULE_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.threats.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import Avatar from '@/components/Avatar.vue';
import GuideButton from '@/components/guide/GuideButton.vue';


export default {
  name: 'RetentionRuleView',
  data: function() {
    return {
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('maintenance.title'),
          to: '/maintenance'
        },
        {
          text: this.$t('maintenance.threats.title'),
          to: '/maintenance/threats'
        }
      ],
      retentionRule: {},
      showInput: false,
      showSingleLinkLabel: true,
      inputLink: '',
      itemToDelete: {},
      rrForm: {
        name: '',
        description: '',
        actors: [],
        client:'',
      },
      error: '',
      timeLimitItems: [],
      timeLimitInput: '',
      buttonDisabled: false,
      selectedFile: {},
      selectedLink: {},
      showDeleteAttachmentModal: false,
      showDeleteLinkModal: false,
      myGuides: [],
      actors: [],
      selectedActors: [],
      newActors: []
    }
  },
  computed: {
    getClient() {
      return this.$store.state.client
    }
  },
  watch: {
    // Watch when client changes in store and trigger api reload
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug !== oldValue.slug) {
        this.goToList()
      } else {
        this.loadContent();
      }
      this.loadActors();
    }
  },
  components: {
    Avatar,
    GuideButton
  },
  methods: {
    goToList() {
      this.$router.push('/maintenance/threats')
    },
    resetFile() {
      let imagefile = document.querySelector('#file');
      imagefile.value = '';
    },
    getGuide(code) {
      let existingGuide = _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Maintenance' && object.enabled
      });
      return existingGuide
    },
    loadGuides(code) {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: 'Maintenance',
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
        }
      }, function() {});
    },
    addActor(newActor) {
      const actor = {
        name: newActor,
        translatable: newActor,
        name_translated: newActor
      }
      this.selectedActors.push(actor)
      this.newActors.push(actor)
    },
    loadContent() {
      let self = this;
      piincHttp.get('threats/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.retentionRule = response.data;
        // Push content name in breadcrumb array
        self.breadcrumbItems.push({text: self.retentionRule.name, active: true});
      }, function() {});
    },
    getUserAvatar(user){
      if (user && user.avatar) {
        return user.avatar;
      } else {
        return require('@/assets/avatar.png');
      }
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-retention-rule'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {}
      this.$refs['delete-retention-rule'].hide()
    },
    confirmDeleteRR(object) {
      let self = this;
      piincHttp.delete('threats/' + object.slug).then(function() {
        self.hideConfirmDeleteModal();
        self.goToList();
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('maintenance.threats.delete.success'))
      }, function() {});
    },
    showEditRRModal(object) {
      this.error = ''
      let self = this;

      if (object.actors && object.actors.length) {
        this.selectedActors = _.cloneDeep(object.actors);
      }

      this.rrForm.slug = object.slug;
      this.rrForm.name = object.name;
      this.rrForm.description = object.description;

      this.$refs['edit-retention-rule'].show()
    },
    hideEditRRModal() {
      this.selectedActors = []
      this.$refs['edit-retention-rule'].hide()
    },
    editRRFormSubmit(evt) {
      this.buttonDisabled = true;
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();
      this.rrForm.client = this.getClient.slug;

      this.rrForm.actors = [];
      if (this.selectedActors && this.selectedActors.length) {
        for (var i = 0; i < this.selectedActors.length; i++) {
          this.rrForm.actors.push(this.selectedActors[i].name);
        }
      }

      if (this.selectedActors && this.selectedActors.length) {
        piincHttp.put('threats/' + this.rrForm.slug, this.rrForm).then(function(response) {
          self.hideEditRRModal();
          self.buttonDisabled = false;
          // If slug is not changed, just reload content
          if (response.data.slug === self.retentionRule.slug) {
            // Remove last item in breadcrumbs, because it will be added again on load content
            self.breadcrumbItems.pop();
            self.loadContent();
          } else {
            // If slug changed, reload state with new param
            self.$router.push({ name: 'maintenance.threats.view', params: { slug: response.data.slug }})
          }
          self.$toastr('success', self.$t('maintenance.threats.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      } else {
        this.error = this.$t('maintenance.threats.create.actors_required')
        this.buttonDisabled = false;
      }
    },
    loadActors(query) {
      let self = this;
      piincHttp.get('actors', { params:
        {
          client: this.getClient.slug,
          keyword: query ? query : undefined
        }
      }).then(function(response) {
        self.actors = response.data.items
        for (var i = 0; i < self.newActors.length; i++) {
          self.actors.push(self.newActors[i]);
        }
        if (self.actors && !self.actors.length && !query) {
          self.actors.push({name: self.$t('system.no_results'), $isDisabled: true})
        }
      }, function() {});
    }
  },
  created () {
    if (this.getClient && this.getClient.slug) {
      this.loadContent();
      this.loadActors();
    }
    this.loadGuides('threat');
  }
}
</script>
